import { yupResolver } from '@hookform/resolvers/yup';
import { FormControlLabel, InputAdornment, Switch, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { Control, useForm, useWatch } from 'react-hook-form';
import { Autocomplete, Input, Upload } from '../../../../../../components';
import { ServiceType } from '../../../../../../entities';
import useStyles from './styles';
import { basicDataSchema, ServiceBasicData } from '../../../../../../validations/service.validation';
import { StepperContentProps } from './Stepper';
import { currencyMask, fromCurrency } from '../../../../../../utils/currency';

const options: Record<ServiceType, string> = {
  [ServiceType.Bike]: 'Bicicletário',
  [ServiceType.ChefStudio]: 'Chef Studio',
  [ServiceType.Coworking]: 'Coworking',
  [ServiceType.GameRoom]: 'Salão de jogos',
  [ServiceType.Grill]: 'Churrasqueira',
  [ServiceType.Default]: 'Padrão',
  [ServiceType.Gym]: 'Academia',
  [ServiceType.HairStylist]: 'Salão de beleza',
  [ServiceType.Massage]: 'Massagem',
  [ServiceType.MeetingRoom]: 'Sala de reunião',
  [ServiceType.Washer]: 'Lavanderia',
  [ServiceType.SteamRoom]: 'Sauna',
  [ServiceType.PartyRoom]: 'Salão de festas',
  [ServiceType.SquashCourt]: 'Quadra de squash',
  [ServiceType.TenisCourt]: 'Quadra de tênis',
  [ServiceType.MultiSportCourt]: 'Quadra poliesportiva',
  [ServiceType.FightRoom]: 'Salão de luta',
  [ServiceType.Pool]: 'Piscina',
};

type BasicInfoProps = Omit<StepperContentProps, 'activeStep'>;

export const BasicInfo = ({
  service,
  shouldValidate,
  file,
  validate,
  setShouldValidate,
  handleUpload,
  handleSetSelectedService,
}: BasicInfoProps): JSX.Element => {
  const styles = useStyles();
  const { palette, spacing } = useTheme();
  const [isPriced, setIsPriced] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<ServiceBasicData>({
    mode: 'onChange',
    resolver: yupResolver(basicDataSchema),
  });

  /** Handle reactive data */
  const type = useWatch({ control, name: 'type' });
  const isEnabled = useWatch({ control, name: 'isEnabled' });

  useEffect(() => {
    reset({
      label: service.label || '',
      price: service.price ? service.price.toString() : '',
      type: service.type as ServiceType,
      isEnabled: service.isEnabled,
    });
    setIsPriced(Boolean(service.price));
  }, [service, reset]);

  useEffect(() => {
    if (shouldValidate) {
      const handle = handleSubmit(() => {
        const values = getValues();
        handleSetSelectedService({ ...values, price: values.price ? fromCurrency(values.price) : undefined });
        validate(true);
      });

      handle().then(() => {
        setShouldValidate(false);
      });
    }
  }, [shouldValidate, getValues, handleSetSelectedService, validate, handleSubmit, setShouldValidate]);

  const handleIsPricedChange = () => {
    setIsPriced(!isPriced);
    setValue('price', null);
  };

  return (
    <div className={styles.container}>
      <Input
        label="nome de área comum"
        placeholder="Ex: Salão de festas"
        validationRegister={register('label')}
        control={control as unknown as Control}
        error={Boolean(errors.label)}
        helperText={errors.label?.message || ' '}
      />

      <Autocomplete
        label="selecione o tipo"
        placeholder="Ex: Salão de festas"
        options={Object.keys(options)}
        getOptionLabel={(value) => options[value as ServiceType]}
        value={type || null}
        error={Boolean(errors.type)}
        helperText={errors.type?.message || ' '}
        onChange={(_, value) => {
          setValue('type', value as ServiceType);
        }}
      />

      <div style={{ marginBottom: spacing(3) }}>
        <Upload file={file || service.imageUrl} onUpload={handleUpload} />
      </div>

      <div className={styles.line}>
        <FormControlLabel
          label="Ativo"
          style={{ marginLeft: '1px', marginRight: '0' }}
          sx={{ color: palette.grey[100] }}
          control={<Switch checked={isEnabled || false} value={isEnabled || false} onChange={() => setValue('isEnabled', !isEnabled)} />}
        />
      </div>

      <div className={styles.line}>
        <FormControlLabel
          label="Espaço pago"
          style={{ marginLeft: '1px', marginRight: '0' }}
          sx={{ color: palette.grey[100] }}
          control={<Switch checked={isPriced} value={isPriced} onChange={handleIsPricedChange} />}
        />
        {isPriced && (
          <Input
            type="text"
            placeholder="Ex: 00,00"
            validationRegister={register('price')}
            control={control as unknown as Control}
            mask={{ ...currencyMask }}
            startIcon={<InputAdornment position="start">R$</InputAdornment>}
            style={{ flex: '1 1 0' }}
            label="preço"
          />
        )}
      </div>
    </div>
  );
};

export default BasicInfo;
